import type {SetAttributeRequest} from './attribute-definition'
import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'

/**
 * Code of the AP "name" dynamic attribute definition seeded for every system
 * type the unified-shape create flow targets. The create form sends this code
 * as `attributeName` on `SetAttributeRequest`; the backend matches it to the
 * active definition and feeds the value into the system type's
 * `displayNameTemplate` to derive the display name (issue 1126).
 */
export const NAME_ATTRIBUTE_CODE = 'name'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

import type {InitialOwnerRequest, OwnerResponse} from '@/types/owner'

export const AccessProfileStatus = {
    ACTIVE: 'ACTIVE',
    DEPRECATED: 'DEPRECATED',
} as const;
export type AccessProfileStatus = typeof AccessProfileStatus[keyof typeof AccessProfileStatus];

export const RiskLevel = {
    CRITICAL: 'CRITICAL',
    HIGH: 'HIGH',
    MEDIUM: 'MEDIUM',
    LOW: 'LOW',
} as const;
export type RiskLevel = typeof RiskLevel[keyof typeof RiskLevel];


/**
 * Narrow list-payload shape for access profiles — mirrors the backend
 * `AccessProfileListItem`. Rendered by the access profiles list page and by
 * cross-page search-results tables that surface `expiryRequired` /
 * `maxDurationDays` per row (see 1120.06.99 finding 1).
 */
export interface AccessProfileListItem {
  id: UUID;
  resource: EntityLink;
  environment: string;
  code: string;
  name: string;
  description: string | null;
  riskLevel: string;
  status: AccessProfileStatus;
  requestable: boolean;
  autoAssignable: boolean;
  expiryRequired: boolean;
  maxDurationDays: number | null;
  createdAt: Instant;
}

/**
 * Wide detail-payload shape for access profiles — extends
 * `AccessProfileListItem` and mirrors the backend `AccessProfileDetail`.
 * Returned by the detail / create / update / activate / deprecate endpoints.
 */
export interface AccessProfile extends AccessProfileListItem {
  reviewFrequencyDays: number;
  owner: EntityLink | null;
  version: number;
  updatedAt: Instant;
}

/**
 * Access profile summary for embedding in other responses.
 */
export interface AccessProfileSummary {
  id: UUID;
  name: string;
  environment: string;
  status: AccessProfileStatus;
}

/**
 * Access profile owner response from the API.
 */
export interface AccessProfileOwner extends OwnerResponse {
  accessProfileId: UUID;
}

/**
 * Access profile binding response from the API.
 */
export interface AccessProfileBinding {
  id: UUID;
  accessProfileId: UUID;
  targetSystem: EntityLink;
  enforcementObject: EntityLink;
  enforcementType: string;
  operation: string;
  createdAt: Instant;
}

/**
 * Request to create a new access profile.
 *
 * Issue 1126: no free-text `name` / `displayName`. The display name is derived
 * from the typed `attributes` list against the system type's
 * `displayNameTemplate`.
 */
export interface AccessProfileCreateRequest {
  resourceId: UUID;
  environment: string;
  code: string;
  attributes: SetAttributeRequest[];
  description?: string;
  riskLevel: string;
  reviewFrequencyDays: number;
  requestable: boolean;
  autoAssignable: boolean;
  expiryRequired: boolean;
  maxDurationDays?: number;
  owners?: InitialOwnerRequest[];
}

/**
 * Request to update an access profile.
 *
 * Issue 1126: update no longer carries a free-text `name`. Display name is
 * derived from typed attributes and is mutated through the attribute editor,
 * not this DTO.
 */
export interface AccessProfileUpdateRequest {
  code?: string;
  description?: string;
  riskLevel: string;
  reviewFrequencyDays: number;
  requestable: boolean;
  autoAssignable: boolean;
  expiryRequired: boolean;
  maxDurationDays?: number;
  version: number;
}

/**
 * Request to create a binding.
 */
export interface AccessProfileBindingCreateRequest {
  accessProfileId: UUID;
  systemId: UUID;
  enforcementObjectId: UUID;
  operation: string;
}

/**
 * Request to create/update an access profile binding (backend record name).
 */
export interface AccessProfileBindingRequest {
  accessProfileId: UUID;
  systemId: UUID;
  enforcementObjectId: UUID;
  operation: string;
}

/**
 * Request to create/update an access profile owner.
 */
export interface AccessProfileOwnerRequest {
  accessProfileId: UUID;
  ownerPartyId: UUID;
  responsibility: string;
  validFrom?: Instant;
  validTo?: Instant;
}

/**
 * Paginated response for access profiles.
 */
export interface AccessProfilePage {
  content: AccessProfile[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface InheritedAccessProfile {
    id: UUID
    resource: EntityLink
    environment: string
    name: string
    description: string | null
    riskLevel: string
    requestable: boolean
    autoAssignable: boolean
    status: AccessProfileStatus
    inherited: boolean
    sourceResource: EntityLink | null
    inheritanceDepth: number
    version: number
    createdAt: Instant
    updatedAt: Instant
}

export interface Owner {
    id: UUID
    parentEntityId: UUID
    ownerPartyId: UUID
    ownerPartyName: string
    responsibility: string
    validFrom: Instant
    validTo: Instant | null
    isOpenEnded: boolean
    isActive: boolean
    version: number
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AccessProfileBindingRequestFields = {
    accessProfileId: 'accessProfileId',
    systemId: 'systemId',
    enforcementObjectId: 'enforcementObjectId',
    operation: 'operation',
} as const

export const AccessProfileBindingResponseFields = {
    id: 'id',
    accessProfileId: 'accessProfileId',
    targetSystem: 'targetSystem',
    enforcementObject: 'enforcementObject',
    enforcementType: 'enforcementType',
    operation: 'operation',
    createdAt: 'createdAt',
} as const

export const AccessProfileCreateRequestFields = {
    resourceId: 'resourceId',
    environment: 'environment',
    code: 'code',
    attributes: 'attributes',
    description: 'description',
    riskLevel: 'riskLevel',
    reviewFrequencyDays: 'reviewFrequencyDays',
    requestable: 'requestable',
    autoAssignable: 'autoAssignable',
    expiryRequired: 'expiryRequired',
    maxDurationDays: 'maxDurationDays',
    owners: 'owners',
} as const

export const AccessProfileOwnerRequestFields = {
    accessProfileId: 'accessProfileId',
    ownerPartyId: 'ownerPartyId',
    responsibility: 'responsibility',
    validFrom: 'validFrom',
    validTo: 'validTo',
} as const

export const AccessProfileOwnerResponseFields = {
    id: 'id',
    accessProfileId: 'accessProfileId',
    ownerPartyId: 'ownerPartyId',
    ownerPartyName: 'ownerPartyName',
    responsibility: 'responsibility',
    validFrom: 'validFrom',
    validTo: 'validTo',
    isOpenEnded: 'isOpenEnded',
    isActive: 'isActive',
    version: 'version',
} as const

export const AccessProfileUpdateRequestFields = {
    code: 'code',
    description: 'description',
    riskLevel: 'riskLevel',
    reviewFrequencyDays: 'reviewFrequencyDays',
    requestable: 'requestable',
    autoAssignable: 'autoAssignable',
    expiryRequired: 'expiryRequired',
    maxDurationDays: 'maxDurationDays',
    version: 'version',
} as const

export const InheritedAccessProfileResponseFields = {
    id: 'id',
    resource: 'resource',
    environment: 'environment',
    name: 'name',
    description: 'description',
    riskLevel: 'riskLevel',
    requestable: 'requestable',
    autoAssignable: 'autoAssignable',
    status: 'status',
    inherited: 'inherited',
    sourceResource: 'sourceResource',
    inheritanceDepth: 'inheritanceDepth',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const
/* eslint-enable i18next/no-literal-string */
